import { action, observable, makeObservable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';

const STORAGE_KEY = 'instoreEnrollmentStatus';
const SEEN_SUCCESS_KEY = 'instoreEnrollmentSeenSuccess';
const DECLINED_LOYALTY_KEY = 'instoreEnrollmentDeclinedLoyalty';
const DECLINED_PAY_BY_BANK_KEY = 'instoreEnrollmentDeclinedPayByBank';
const FULLY_ENROLLED_KEY = 'instoreEnrollmentFullyEnrolled';
const COMPLETED_MED_INTAKE_KEY = 'instoreEnrollmentCompletedMedIntake';
const COMPLETED_LOYALTY_STEP_KEY = 'instoreEnrollmentCompletedLoyaltyStep';
const SESSION_ID_KEY = 'instoreEnrollmentSessionId';

export enum InstoreEnrollmentStatus {
  ecommLogin = 'ECOMM_LOGIN',
  medIntake = 'MED_INTAKE',
  payByBankEnrollment = 'PAY_BY_BANK_ENROLLMENT',
  connectBankAccount = 'CONNECT_BANK_ACCOUNT',
  loyaltyEnrollment = 'LOYALTY_ENROLLMENT',
  finishedEnrollment = 'FINISHED_ENROLLMENT',
}

export class InstoreEnrollmentStore {
  @observable currentStatus: InstoreEnrollmentStatus | null = null;
  @observable declinedLoyalty = false;
  @observable declinedPayByBank = false;
  @observable hasSeenSuccess = false;
  @observable hasValidMedCard = false;
  @observable fullyEnrolled = false;
  @observable completedMedIntake = false;
  @observable completedLoyaltyStep = false;
  @observable userPhone: string | null = null;
  @observable enrollmentSessionId: string = uuidv4();

  constructor() {
    makeObservable(this);

    this.currentStatus =
      this.loadPersistedValue(STORAGE_KEY, Object.values(InstoreEnrollmentStatus)) ?? this.currentStatus;
    this.hasSeenSuccess = this.loadPersistedValue(SEEN_SUCCESS_KEY) ?? this.hasSeenSuccess;
    this.declinedLoyalty = this.loadPersistedValue(DECLINED_LOYALTY_KEY) ?? this.declinedLoyalty;
    this.declinedPayByBank = this.loadPersistedValue(DECLINED_PAY_BY_BANK_KEY) ?? this.declinedPayByBank;
    this.completedMedIntake = this.loadPersistedValue(COMPLETED_MED_INTAKE_KEY) ?? this.completedMedIntake;
    this.completedLoyaltyStep = this.loadPersistedValue(COMPLETED_LOYALTY_STEP_KEY) ?? this.completedLoyaltyStep;
    this.fullyEnrolled = this.loadPersistedValue(FULLY_ENROLLED_KEY) ?? this.fullyEnrolled;
    this.enrollmentSessionId = this.loadPersistedValue(SESSION_ID_KEY) ?? this.enrollmentSessionId;
    setPersistedValue(SESSION_ID_KEY, this.enrollmentSessionId, true);
  }

  private loadPersistedValue<T>(key: string, validValues?: T[]): T | null {
    const savedValue = getPersistedValue(key);

    if (!savedValue) {
      return null;
    }

    if (validValues && !validValues.includes(savedValue)) {
      return null;
    }

    return savedValue;
  }

  @action
  setCurrentStatus(status: InstoreEnrollmentStatus): void {
    this.currentStatus = status;
    setPersistedValue(STORAGE_KEY, status, true);
  }

  @action
  reset(): void {
    this.currentStatus = InstoreEnrollmentStatus.ecommLogin;
    this.userPhone = null;
    this.completedMedIntake = false;
    this.completedLoyaltyStep = false;
    this.declinedLoyalty = false;
    this.declinedPayByBank = false;
    this.hasSeenSuccess = false;
    this.hasValidMedCard = false;
    this.fullyEnrolled = false;
    this.enrollmentSessionId = uuidv4();

    // Clear all persisted values
    setPersistedValue(STORAGE_KEY, InstoreEnrollmentStatus.ecommLogin, true);
    setPersistedValue(COMPLETED_MED_INTAKE_KEY, false, true);
    setPersistedValue(COMPLETED_LOYALTY_STEP_KEY, false, true);
    setPersistedValue(DECLINED_LOYALTY_KEY, false, true);
    setPersistedValue(DECLINED_PAY_BY_BANK_KEY, false, true);
    setPersistedValue(SEEN_SUCCESS_KEY, false, true);
    setPersistedValue(FULLY_ENROLLED_KEY, false, true);
    setPersistedValue(SESSION_ID_KEY, this.enrollmentSessionId, true);
  }

  @action
  setDeclinedLoyalty(declined: boolean): void {
    this.declinedLoyalty = declined;
    setPersistedValue(DECLINED_LOYALTY_KEY, declined, true);
  }

  @action
  setDeclinedPayByBank(declined: boolean): void {
    this.declinedPayByBank = declined;
    setPersistedValue(DECLINED_PAY_BY_BANK_KEY, declined, true);
  }

  @action
  setHasSeenSuccess(): void {
    this.hasSeenSuccess = true;
    setPersistedValue(SEEN_SUCCESS_KEY, true, true);
  }

  @action
  setDidDeclinePayByBank(): void {
    this.declinedPayByBank = true;
    setPersistedValue(DECLINED_PAY_BY_BANK_KEY, true, true);
  }

  @action
  setDidNotDeclinePayByBank(): void {
    this.declinedPayByBank = false;
    setPersistedValue(DECLINED_PAY_BY_BANK_KEY, false, true);
  }

  @action
  setHasValidMedCard(valid: boolean): void {
    this.hasValidMedCard = valid;
  }

  @action
  setIsFullyEnrolled(): void {
    this.fullyEnrolled = true;
    setPersistedValue(FULLY_ENROLLED_KEY, true, true);
  }

  @action
  setUserPhone(phone: string | null): void {
    this.userPhone = phone;
  }

  @action
  setCompletedMedIntake(completed: boolean): void {
    this.completedMedIntake = completed;
    setPersistedValue(COMPLETED_MED_INTAKE_KEY, completed, true);
  }

  @action
  setCompletedLoyaltyStep(completed: boolean): void {
    this.completedLoyaltyStep = completed;
    setPersistedValue(COMPLETED_LOYALTY_STEP_KEY, completed, true);
  }
}
